import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';
import MicButton from './MicButton';
import { BRANDS } from '../lib/brands';
import { getCurrentUser } from '../services/auth';

/**
 * Where a film starts: say what you want, and it writes the script.
 *
 * ⚠️ THIS IS THE ONLY FREE STAGE. Writing a script costs a fraction of a cent.
 * Everything after it - footage, voice, music, rendering - is billed, which is
 * why the brief asks for nothing but the idea and every later step is a gate
 * somebody passes deliberately.
 *
 * ⚠️ IT ASKS FOR LENGTH IN SECONDS, NOT WORDS. The narration is read at about
 * 120 words a minute and the film is cut to the voice, so seconds is the number
 * that actually decides the shape of what comes out.
 */

const FN = '/.netlify/functions/video-studio';

const LENGTHS = [30, 45, 60, 90, 120];

export default function NewFilmBrief({ onClose, onStarted }) {
  const [brief, setBrief] = useState('');
  const [title, setTitle] = useState('');
  const [brand, setBrand] = useState('crab-place');
  const [seconds, setSeconds] = useState(60);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const start = async () => {
    setBusy(true); setErr(null);
    try {
      const user = getCurrentUser();
      const res = await fetch(FN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && user.accessToken ? { Authorization: `Bearer ${user.accessToken}` } : {}),
        },
        body: JSON.stringify({ action: 'start', brief, title, brand, seconds }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `start failed (${res.status})`);
      onStarted(d.projectId);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 640, width: '100%' }}>
        <div className="card-header">New film</div>
        <div className="card-body">
          {err ? (
            <div style={{ background: '#fdecec', color: '#c0392b', padding: '8px 12px', borderRadius: 5, marginBottom: 12, fontSize: 12 }}>{err}</div>
          ) : null}

          <div style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6, marginBottom: 16 }}>
            Describe the film you want. It writes the script first, and you read that before anything
            else happens.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="form-label" style={{ marginBottom: 0 }}>What is the film about?</label>
            <MicButton
              title="Dictate the brief"
              onTranscript={(t) => setBrief((cur) => (cur ? `${cur.trimEnd()} ${t}` : t))}
            />
          </div>
          <textarea className="form-textarea" rows={5} value={brief} onChange={(e) => setBrief(e.target.value)}
            placeholder="A film about why Crisfield is the crab capital of the world, and why that matters when you order steamed crabs from us. The watermen, the working harbour, the tradition."
            style={{ marginBottom: 14 }} />

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: '1 1 220px' }}>
              <label className="form-label">Working title <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>(optional)</span></label>
              <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="taken from the brief if you leave it" />
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <label className="form-label">Brand</label>
              <select className="form-select" value={brand} onChange={(e) => setBrand(e.target.value)}>
                {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <div style={{ flex: '0 0 120px' }}>
              <label className="form-label">Length</label>
              <select className="form-select" value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}>
                {LENGTHS.map((n) => <option key={n} value={n}>{n} seconds</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 14 }}>
            Writing the script costs almost nothing. Footage, voice and rendering are billed, and each is
            a separate approval after you have read what it wrote.
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || !brief.trim()} onClick={start}>
              <Wand2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
              {busy ? 'Starting…' : 'Write the script'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
